import { toLocale } from '@repo/i18n/i18n-resources/language-utils';
import type {
    CreateMap3dObjectRequest,
    MapData3dObject,
    UpdateMap3dObjectRequest,
    Upload3dObjectResponse,
} from '@repo/types/maps/map-3d-types';
import type {
    CreateMapLabelRequest,
    CreateMapRegionRequest,
    CreateUserCustomMarkerRequest,
    CreateUserCustomMarkerResponse,
    CustomPinProperties,
    GameMapInfo,
    GameMapUserPin,
    MapConfig,
    MapData,
    MapDataCategory,
    MapDataHeroAction,
    MapDataLabel,
    MapDataLocationFeature,
    MapDataLocationFeatureProperties,
    MapDataLocationFeatureSessionProperties,
    MapDataRegion,
    MapDataSubcategory,
    MapPinSyncResponse,
    NewMapDataLocationFeature,
    UpdateMapLabelRequest,
    UpdateMapRegionRequest,
    UpdateUserCustomMarkerRequest,
    UploadLabelIconResponse,
} from '@repo/types/maps/map-types';
import { WeModApiClient } from './api-client';

const apiClient = new WeModApiClient(process.env.NEXT_PUBLIC_WEMOD_API_URL ?? '');

export interface HeroActionSuggestion {
    name?: string;
    description?: string | null;
    icon_category?: string;
    priority?: number;
    result_pin_external_ids?: string[];
    trigger_pin_external_ids?: string[];
    rationale?: string | null;
}

export interface HeroActionPin {
    id: number;
    externalId: string;
    title: string;
    description: string | null;
    gameMapId: number;
    gameMapExternalId: string;
    gameMapName: string;
    subcategory: string | null;
}

export interface HeroActionPreview {
    valid: boolean;
    normalized: {
        name: string;
        description: string | null;
        iconCategory: string;
        priority: number;
        resultPinExternalIds: string[];
        triggerPinExternalIds: string[];
        rationale: string | null;
    } | null;
    warnings: { code: string; message: string }[];
    droppedPinExternalIds: {
        result: string[];
        trigger: string[];
    };
    pins: {
        result: HeroActionPin[];
        trigger: HeroActionPin[];
    };
}

export interface HeroActionRequest {
    titleId?: string;
    gameMapExternalId?: string;
    heroActionExternalId?: string;
    lockedMapExternalIds?: string[];
    lockToCurrentMap?: boolean;
    keepCurrentPins?: boolean;
    description?: string;
    prompt?: string;
    maxTokens?: number;
    maxSteps?: number;
    timeoutSeconds?: number;
    temperature?: number;
    enableWebSearch?: boolean;
    webSearchMaxUses?: number;
}

export interface HeroActionUpdateRequest {
    titleId?: string;
    gameMapExternalId?: string;
    lockedMapExternalIds?: string[];
    lockToCurrentMap?: boolean;
    suggestion: HeroActionSuggestion;
}

export interface HeroActionPreviewResponse {
    title: {
        id: number;
        name: string;
        slug: string;
    };
    inScopeMaps: {
        id: number;
        externalId: string;
        name: string;
    }[];
    rawSuggestion: HeroActionSuggestion | null;
    preview: HeroActionPreview;
}

export type HeroActionGenerationState = 'queued' | 'running' | 'completed' | 'failed' | 'timed_out' | 'canceled';

export interface HeroActionGeneration {
    id: string;
    state: HeroActionGenerationState;
    message: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    expiresAt: string;
    timeoutSeconds: number;
    error: string | null;
    result: HeroActionPreviewResponse | null;
}

export interface HeroActionGenerationResponse {
    generation: HeroActionGeneration;
}

export interface HeroActionMutationResponse {
    heroAction: {
        id: number;
        externalId: string;
        name: Record<string, string>;
        description: Record<string, string>;
        iconCategory: string;
        priority: number;
        isApproved: boolean;
        resultPinExternalIds: string[];
        triggerPinExternalIds: string[];
    };
    preview: HeroActionPreview;
}

export interface HeroActionListResponse {
    title: {
        id: number;
        name: string;
        slug: string;
    };
    heroActions: MapDataHeroAction[];
}

export async function listHeroActions(
    request: Pick<HeroActionRequest, 'titleId' | 'gameMapExternalId'>,
): Promise<HeroActionListResponse> {
    return apiClient.get<HeroActionListResponse>('/v3/game_map/hero_actions', {
        params: request,
    });
}

export async function startHeroActionGeneration(request: HeroActionRequest): Promise<HeroActionGenerationResponse> {
    return apiClient.post<HeroActionGenerationResponse>('/v3/game_map/hero_actions/generate', request);
}

export async function getHeroActionGeneration(generationId: string): Promise<HeroActionGenerationResponse> {
    return apiClient.get<HeroActionGenerationResponse>(`/v3/game_map/hero_actions/generate/${generationId}`);
}

export async function cancelHeroActionGeneration(generationId: string): Promise<HeroActionGenerationResponse> {
    return apiClient.delete<HeroActionGenerationResponse>(`/v3/game_map/hero_actions/generate/${generationId}`);
}

export async function createHeroAction(
    request: Pick<HeroActionRequest, 'titleId' | 'gameMapExternalId' | 'lockedMapExternalIds' | 'lockToCurrentMap'> & {
        suggestion: HeroActionSuggestion;
    },
): Promise<HeroActionMutationResponse> {
    return apiClient.post<HeroActionMutationResponse>('/v3/game_map/hero_actions', request);
}

export async function updateHeroAction(
    externalId: string,
    request: HeroActionUpdateRequest,
): Promise<HeroActionMutationResponse> {
    return apiClient.put<HeroActionMutationResponse>(`/v3/game_map/hero_actions/${externalId}`, request);
}

export async function getGameMaps(
    titleParam: { titleId?: string; titleSlug?: string },
    locale: string,
    options?: { debug?: boolean; token?: string },
): Promise<readonly GameMapInfo[]> {
    const headers: Record<string, string> = {
        'Accept-Language': toLocale(locale),
    };

    if (!titleParam.titleId && !titleParam.titleSlug) {
        throw new Error('Either titleId or titleSlug must be provided');
    }

    if (options?.token) {
        headers.Authorization = `Bearer ${options.token}`;
    }

    return apiClient.get<readonly GameMapInfo[]>('/v3/game_maps', {
        params: {
            ...(titleParam.titleId && { title_id: titleParam.titleId }),
            ...(titleParam.titleSlug && { title_slug: titleParam.titleSlug }),
            locale: toLocale(locale),
            ...(options?.debug && { debug: options.debug }),
        },
        headers,
        retry404: options?.debug,
    });
}

export async function getGameMap(
    externalId: string,
    locale: string,
    options?: { debug?: boolean; token?: string },
): Promise<MapData> {
    const headers: Record<string, string> = {
        'Accept-Language': toLocale(locale),
    };

    if (options?.token) {
        headers.Authorization = `Bearer ${options.token}`;
    }

    return apiClient.get<MapData>(`/v3/game_maps/${externalId}`, {
        params: {
            locale: toLocale(locale),
            ...(options?.debug && { debug: options.debug }),
        },
        headers,
        retry404: options?.debug,
    });
}

export async function updateMapMetadata(
    externalId: string,
    data: Partial<MapData['metadata']>,
): Promise<{ externalId: string; metadata: MapData['metadata'] }> {
    return apiClient.put<{ externalId: string; metadata: MapData['metadata'] }>(`/v3/game_maps/${externalId}`, data, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function getGameMapMetadata({
    title_slug,
    map_slug,
    locale = 'en-US',
    options,
}: {
    title_slug: string;
    map_slug: string;
    locale: string;
    options?: { debug?: boolean; token?: string };
}): Promise<MapConfig> {
    const headers: Record<string, string> = {
        'Accept-Language': toLocale(locale),
    };

    if (options?.token) {
        headers.Authorization = `Bearer ${options.token}`;
    }

    const data = await apiClient.get<MapConfig>('/v3/game_maps/metadata', {
        params: {
            title_slug,
            map_slug,
            locale: toLocale(locale),
            ...(options?.debug && { debug: options.debug }),
        },
        headers,
        retry404: options?.debug,
    });
    return {
        ...data,
        map: {
            ...data.map,
            debug: data.map.debug ?? Boolean(options?.debug),
        },
    };
}

export async function setMapPinFound(externalId: string, pin: GameMapUserPin): Promise<GameMapUserPin> {
    return apiClient.post<GameMapUserPin>(`/v3/game_map/${externalId}/user-pin-statuses`, pin);
}

export async function getUserMapPins(externalId: string): Promise<GameMapUserPin[]> {
    return apiClient.get<GameMapUserPin[]>(`/v3/game_map/${externalId}/user-pin-statuses`);
}

export async function getUserCustomMarkers(externalId: string): Promise<{ geojson: GeoJSON.FeatureCollection }> {
    return apiClient.get<{ geojson: GeoJSON.FeatureCollection }>(`/v3/game_maps/${externalId}/user`);
}

export async function createUserCustomMarker(
    payload: CreateUserCustomMarkerRequest,
): Promise<CreateUserCustomMarkerResponse> {
    return apiClient.post<CreateUserCustomMarkerResponse>(`/v3/game_map/user/pins`, payload);
}

export async function deleteUserCustomMarker(externalId: string): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/user/pins/${externalId}`);
}

export async function updateUserCustomMarker(
    externalId: string,
    payload: UpdateUserCustomMarkerRequest,
): Promise<void> {
    return apiClient.put<void>(`/v3/game_map/user/pins/${externalId}`, payload);
}

export async function syncMapPins(externalId: string, pinIds: string[]): Promise<MapPinSyncResponse> {
    return apiClient.post<MapPinSyncResponse>(`/v3/game_map/${externalId}/user-pin-statuses/sync`, { pins: pinIds });
}

export async function resetFoundPins(externalId: string): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/${externalId}/user-pin-statuses`);
}

export async function bulkUpdatePinStatuses(
    externalId: string,
    pins: Pick<GameMapUserPin, 'pinnableType' | 'pinnableId'>[],
    found: boolean,
): Promise<MapPinSyncResponse> {
    return apiClient.post<MapPinSyncResponse>(`/v3/game_map/${externalId}/user-pin-statuses/bulk`, {
        found,
        pins,
    });
}

export async function createGameMapCategory(
    mapExternalId: string,
    category: Omit<MapDataCategory, 'externalId'>,
): Promise<MapDataCategory> {
    return apiClient.post<MapDataCategory>('/v3/game_map/categories', {
        gameMapId: mapExternalId,
        ...category,
    });
}

export async function updateGameMapCategory(category: MapDataCategory): Promise<MapDataCategory> {
    return apiClient.put<MapDataCategory>(`/v3/game_map/categories/${category.externalId}`, {
        ...category,
    });
}

export async function deleteGameMapCategory(externalCategoryId: string): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/categories/${externalCategoryId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function createGameMapSubcategory(
    subcategory: Omit<MapDataSubcategory, 'externalId'>,
): Promise<MapDataSubcategory> {
    return apiClient.post<MapDataSubcategory>('/v3/game_map/subcategories', subcategory);
}

export async function updateGameMapSubcategory(subcategory: MapDataSubcategory): Promise<MapDataSubcategory> {
    return apiClient.put<MapDataSubcategory>(`/v3/game_map/subcategories/${subcategory.externalId}`, {
        ...subcategory,
    });
}

export async function deleteGameMapSubcategory(subcategory: MapDataSubcategory): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/subcategories/${subcategory.externalId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function createGameMapLocationFeature(
    gameMapExternalId: string,
    location: NewMapDataLocationFeature,
): Promise<MapDataLocationFeature> {
    const properties: NewMapDataLocationFeature['properties'] = {
        title: location.properties.title ?? '',
        description: location.properties.description ?? '',
        imageId: location.properties.imageId ?? null,
        videoId: location.properties.videoId ?? null,
        imagePath: null,
        videoPath: null,
        subcategoryExternalId: location.properties.subcategoryExternalId,
        gameCoords: location.properties.gameCoords,
        verticalLevel: location.properties.verticalLevel ?? null,
    };

    return apiClient.post<MapDataLocationFeature>('/v3/game_map/pins', {
        ...location,
        properties,
        gameMapExternalId,
    });
}

export async function updateGameMapLocationFeature(location: MapDataLocationFeature): Promise<MapDataLocationFeature> {
    const properties: MapDataLocationFeatureProperties = {
        title: location.properties.title ?? '',
        description: location.properties.description ?? '',
        imageId: location.properties.imageId ?? null,
        videoId: location.properties.videoId ?? null,
        imagePath: location.properties.imagePath ?? null,
        videoPath: location.properties.videoPath ?? null,
        externalId: location.properties.externalId,
        subcategoryExternalId: location.properties.subcategoryExternalId,
        gameCoords: location.properties.gameCoords,
        verticalLevel: location.properties.verticalLevel ?? null,
    };

    return apiClient.put<MapDataLocationFeature>(`/v3/game_map/pins/${location.properties.externalId}`, {
        ...location,
        properties,
    });
}

export interface BulkMoveGameMapPinsRequest {
    anchorPinExternalId: string;
    geometry: {
        type: 'Point';
        coordinates: [number, number];
    };
}

export async function bulkMoveGameMapPins(
    externalId: string,
    request: BulkMoveGameMapPinsRequest,
): Promise<MapData['geojson']> {
    return apiClient.put<MapData['geojson']>(`/v3/game_map/${externalId}/pins/bulk-move`, request, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export interface BulkUpdateGameMapPin {
    readonly externalId: string;
    // bulk-update rewrites the whole pin, so properties must carry every stored field
    // (gameCoords, imagePath, ...) with the pin's current values, overwriting only the
    // edited ones. Session-only flags added on map load are excluded.
    readonly properties: Omit<MapDataLocationFeatureProperties, keyof MapDataLocationFeatureSessionProperties>;
    readonly geometry: MapDataLocationFeature['geometry'];
}

export async function bulkUpdateGameMapPins(
    externalId: string,
    pins: readonly BulkUpdateGameMapPin[],
): Promise<MapData['geojson']> {
    return apiClient.put<MapData['geojson']>(
        `/v3/game_map/${externalId}/pins/bulk-update`,
        { pins },
        {
            headers: {
                'Content-Type': 'application/json',
            },
        },
    );
}

export async function deleteGameMapLocationFeature(location: MapDataLocationFeature): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/pins/${location.properties.externalId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export interface PinMediaResponse {
    image_id?: number;
    video_id?: number;
}

export interface CreatePinMediaRequest {
    externalPinId: string;
    image?: File;
    video?: File;
}

export async function updateMapPinMedia(request: CreatePinMediaRequest): Promise<PinMediaResponse> {
    const body = new FormData();

    if (request.image) {
        body.append('image', request.image);
    }

    if (request.video) {
        body.append('video', request.video);
    }

    return apiClient.post<PinMediaResponse>(`/v3/game_map/pins/${request.externalPinId}/pin_media`, body);
}

export async function getCustomUserPin(externalId: string): Promise<CustomPinProperties> {
    return apiClient.get<CustomPinProperties>(`/v3/game_map/user/pins/${externalId}`);
}

export async function deleteGameMapImage(externalMapId: string, imageId: number): Promise<void> {
    return apiClient.delete<void>(`/v3/game_maps/${externalMapId}/pin_image/${imageId}`);
}

// Region API functions
export async function createGameMapRegion(request: CreateMapRegionRequest): Promise<MapDataRegion> {
    return apiClient.post<MapDataRegion>('/v3/game_map/regions', request);
}

export async function updateGameMapRegion(externalId: string, request: UpdateMapRegionRequest): Promise<MapDataRegion> {
    return apiClient.put<MapDataRegion>(`/v3/game_map/regions/${externalId}`, request);
}

export async function deleteGameMapRegion(externalId: string): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/regions/${externalId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

// Label API functions
export async function createGameMapLabel(request: CreateMapLabelRequest): Promise<MapDataLabel> {
    return apiClient.post<MapDataLabel>('/v3/game_map/labels', request);
}

export async function updateGameMapLabel(externalId: string, request: UpdateMapLabelRequest): Promise<MapDataLabel> {
    return apiClient.put<MapDataLabel>(`/v3/game_map/labels/${externalId}`, request);
}

export async function deleteGameMapLabel(externalId: string): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/labels/${externalId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function uploadLabelIcon(externalId: string, icon: File): Promise<UploadLabelIconResponse> {
    const body = new FormData();
    body.append('icon', icon);

    return apiClient.post<UploadLabelIconResponse>(`/v3/game_map/labels/${externalId}/icon`, body);
}

// 3D Objects
export async function createMap3dObject(request: CreateMap3dObjectRequest): Promise<MapData3dObject> {
    return apiClient.post<MapData3dObject>(`/v3/game_map/3d_objects`, request, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function updateMap3dObject(
    externalId: string,
    request: UpdateMap3dObjectRequest,
): Promise<MapData3dObject> {
    return apiClient.put<MapData3dObject>(`/v3/game_map/3d_objects/${externalId}`, request, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function deleteMap3dObject(externalId: string): Promise<void> {
    return apiClient.delete<void>(`/v3/game_map/3d_objects/${externalId}`, {
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function upload3dObject(externalId: string, model: File): Promise<Upload3dObjectResponse> {
    const body = new FormData();
    body.append('model', model);

    return apiClient.post<Upload3dObjectResponse>(`/v3/game_map/3d_objects/${externalId}/model`, body);
}
